import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_praxis-launch/artifacts/9x9a538r_MeritumPraxis%20Logo.png";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Candidates', path: '/candidates' },
    { name: 'Organizations', path: '/organizations' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="header"
      data-scrolled={isScrolled}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-meritum-navy/95 backdrop-blur-md shadow-md border-b border-meritum-teal/20'
          : 'bg-meritum-navy/80 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <nav className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="MeritumPraxis - Progress Made Visible"
              className="h-20 w-auto object-contain drop-shadow-lg"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`font-body text-sm font-medium transition-colors duration-200 relative ${
                  isActive(link.path)
                    ? 'text-meritum-gold'
                    : 'text-white hover:text-meritum-gold'
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-meritum-gold" />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/login" data-testid="login-btn">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-meritum-navy font-body font-medium px-6"
              >
                Log In
              </Button>
            </Link>
            <Link to="/pricing" data-testid="signup-btn">
              <Button className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-6">
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-20 left-0 w-full bg-meritum-navy border-b border-meritum-teal/20 shadow-lg animate-fade-in"
          >
            <div className="container mx-auto px-4 py-6">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`font-body text-base font-medium py-2 ${
                      isActive(link.path)
                        ? 'text-meritum-gold'
                        : 'text-white hover:text-meritum-gold'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="flex flex-col gap-3 pt-4 border-t border-meritum-teal/20">
                  <Link to="/login">
                    <Button
                      variant="outline"
                      className="w-full border-2 border-white text-white hover:bg-white hover:text-meritum-navy font-body font-medium"
                    >
                      Log In
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button className="w-full bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold">
                      Get Started
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
